<!--
  A popup to edit or create a skill
-->

<template>
  <el-dialog class="skill-editor"
    :title="value ? 'Edit the skill' : 'Create a skill'"
    :visible="visible"
    @update:visible="hide">
    <el-form ref="form"
      :model="model"
      @submit.native.prevent="save"
      label-width="200px"
      :hide-required-asterisk="true">
      <el-form-item prop="name"
        :required="true"
        :rules="ReadableValidator">
        <template slot="label">
          Name
          <tooltip-icon placement="bottom">
            The name of the <em>skill</em>, used to invoke it.
          </tooltip-icon>
        </template>
        <el-input v-model="model.name"></el-input>
      </el-form-item>
      <el-form-item label="Owner" prop="owner"
        v-if="model.owner!==undefined">
        <el-filterable-select style="width: 100%"
          v-model="model.owner"
          :options="users"
          value-field="user"
          label-field="email"></el-filterable-select>
      </el-form-item>
      <el-form-item prop="discrete_publish"
        v-if="model.discrete_publish!==undefined">
        <template slot="label">
          Discrete intent publishing
          <tooltip-icon placement="bottom">
            If the <em>intents</em> of the <em>skill</em>, can be published one by one.<br/>
            Gives finer management, but can create version problems.
          </tooltip-icon>
        </template>
        <el-checkbox v-model="model.discrete_publish"></el-checkbox>
      </el-form-item>
      <el-form-item prop="custom_invoke"
        v-if="model.custom_invoke!==undefined">
        <template slot="label">
          Custom skill invocation
          <tooltip-icon placement="bottom">
            Allows to configure the invocation sentences of the <em>skill</em>.
          </tooltip-icon>
        </template>
        <el-checkbox v-model="model.custom_invoke"></el-checkbox>
      </el-form-item>
      <el-form-item label="Use default skill invocation" prop="default_invoke"
        v-if="model.default_invoke!==undefined">
        <template slot="label">
          Use default skill invocation
          <tooltip-icon placement="bottom">
            Use the default invocation sentences of the <em>skill</em>,<br/>
            in addition of the ones defined in the <em>invoke</em> <em>intent.</em>
          </tooltip-icon>
        </template>
        <el-checkbox v-model="model.default_invoke"></el-checkbox>
      </el-form-item>
      <!-- domains input -->
      <el-form-item
        prop="domains"
        :rules="domainsRules">
        <template slot="label">
          Domains
          <tooltip-icon placement="bottom">
            Select in which <em>domains</em> this <em>skilled</em> can be called.
          </tooltip-icon>
        </template>
        <domains-selector
          v-model="model.domains"
          :domains="state.settings.domains"
          :rules="domainsRules"
          @rules="val => domainsRules = val">
        </domains-selector>
      </el-form-item>
      <div>
        <el-button @click="hide">Cancel</el-button>
        <el-button native-type="submit">{{value ? 'Save' : 'Create'}}</el-button>
        <import-export
          style="float: right"
          v-model="model"
          id-field="skill"
          :object-id="skillid"
          :existing="existing"
          route-name="skill"
          :modal="false"
        ></import-export>
      </div>
    </el-form>
  </el-dialog>
</template>

<script>
import State from '../../stores/state.js'
import ElFilterableSelect from '../element/ElFilterableSelect.vue'
import { ReadableValidator } from '../../utils/validators'
import DomainsSelector from '../DomainsSelector.vue'
import ImportExport from '../ImportExport.vue'

export default {
  name: 'SkillEditor',
  components: {
    ElFilterableSelect,
    DomainsSelector,
    ImportExport,
  },
  props: {
    visible: Boolean,
    value: Object,
    existing: Array,
  },
  data () {
    return {
      state: State.state,
      skillid: null,
      model: {},
      users: [{ email: 'No owner', user: null }],
      ReadableValidator,
      domainsRules: [],
    }
  },
  inject: ['onerror'],
  watch: {
    visible (val) {
      if (!val) return
      let value = this.value || {}
      this.skillid = value.skill
      let model = { name: value.name || '' }
      if (value.skill) model.skill = value.skill
      if (this.state.user && this.state.user.role === 'admin') {
        model.owner = value.owner || null
        model.discrete_publish = !!value.discrete_publish
        model.custom_invoke = !!value.custom_invoke
        model.default_invoke = !!value.default_invoke
        model.domains = value.domains || null
        State.listUsers().then(data => {
          this.users = [{ email: 'No owner', user: null }]
            .concat(data.users.filter(x => x.role === 'client'))
        }).catch(this.onerror)
      }
      this.model = model
    },
  },
  methods: {
    hide () {
      this.$emit('update:visible', false)
    },
    save () {
      this.$refs.form.validate(valid => {
        if (valid) {
          let value = this.value || {}
          let model = Object.assign({}, this.model)
          if (model.discrete_publish === !!value.discrete_publish) {
            delete model.discrete_publish
          }
          if (model.custom_invoke === !!value.custom_invoke) {
            delete model.custom_invoke
          }
          if (model.default_invoke === !!value.default_invoke) {
            delete model.default_invoke
          }
          this.$emit('input', model)
          this.hide()
        } else {
          this.$message.error('Please correct the errors and try again')
        }
      })
    },
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  .skill-editor >.el-dialog {
    min-width: 650px;
  }
</style>
